import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import { Link as RouterLink } from "react-router-dom";

export const APP_TRACKING_CONSENT_STORAGE_KEY =
  "accounts.app.pixel-measurement-consent.v2";

const META_PIXEL_SCRIPT_URL =
  "https://connect.facebook.net/en_US/fbevents.js";
const TIKTOK_PIXEL_SCRIPT_URL =
  "https://analytics.tiktok.com/i18n/pixel/events.js";
const TIKTOK_METHODS = [
  "page",
  "track",
  "identify",
  "instances",
  "debug",
  "on",
  "off",
  "once",
  "ready",
  "alias",
  "group",
  "enableCookie",
  "disableCookie",
  "holdConsent",
  "revokeConsent",
  "grantConsent",
];
const UNKNOWN = "unknown";
const GRANTED = "granted";
const DENIED = "denied";
const STORED_DECISIONS = new Set([GRANTED, DENIED]);
const META_PIXEL_STATE = Symbol("appDownloadMetaPixelState");
const TIKTOK_PIXEL_STATE = Symbol("appDownloadTikTokPixelState");
const NO_ACTIVE_TRACKING = Object.freeze({ meta: false, tikTok: false });

const COPY = Object.freeze({
  RO: {
    title: "Analiză opțională",
    settingsTitle: "Opțiuni de confidențialitate",
    summaryBoth:
      "Pixelurile Meta și TikTok măsoară această vizită și clicurile spre magazine. Pot primi date tehnice ale browserului; nu trimitem explicit câmpuri despre client sau cont. Linkurile funcționează fără măsurare.",
    summaryMeta:
      "Meta Pixel măsoară această vizită și clicurile spre magazine. Meta poate primi date tehnice ale browserului; nu trimitem explicit câmpuri despre client sau cont. Linkurile funcționează fără măsurare.",
    summaryTikTok:
      "TikTok Pixel măsoară această vizită și clicurile spre magazine. TikTok poate primi date tehnice ale browserului; nu trimitem explicit câmpuri despre client sau cont. Linkurile funcționează fără măsurare.",
    accept: "Permite",
    refuse: "Nu, mulțumesc",
    settings: "Opțiuni de confidențialitate",
    close: "Închide",
    privacy: "Detalii",
    newTab: "se deschide într-o filă nouă",
    storageError:
      "Alegerea nu a putut fi salvată. Măsurarea rămâne dezactivată.",
  },
  EN: {
    title: "Optional analytics",
    settingsTitle: "Privacy choices",
    summaryBoth:
      "Meta and TikTok Pixels measure this visit and store-link clicks. They may receive technical browser data; we do not explicitly send customer or account fields. The links work without measurement.",
    summaryMeta:
      "Meta Pixel measures this visit and store-link clicks. Meta may receive technical browser data; we do not explicitly send customer or account fields. The links work without measurement.",
    summaryTikTok:
      "TikTok Pixel measures this visit and store-link clicks. TikTok may receive technical browser data; we do not explicitly send customer or account fields. The links work without measurement.",
    accept: "Allow",
    refuse: "No thanks",
    settings: "Privacy choices",
    close: "Close",
    privacy: "Details",
    newTab: "opens in a new tab",
    storageError:
      "Your choice could not be saved. Measurement remains disabled.",
  },
});

export function getConfiguredAppDownloadMetaPixelId(value) {
  const pixelId = String(value || "").trim();
  return /^[1-9]\d*$/.test(pixelId) ? pixelId : "";
}

export function getConfiguredAppDownloadTikTokPixelId(value) {
  const pixelId = String(value || "").trim().toUpperCase();
  return /^[A-Z0-9]{20}$/.test(pixelId) ? pixelId : "";
}

function readConsent() {
  try {
    const decision = window.localStorage.getItem(
      APP_TRACKING_CONSENT_STORAGE_KEY
    );
    return STORED_DECISIONS.has(decision) ? decision : UNKNOWN;
  } catch (error) {
    return UNKNOWN;
  }
}

function writeConsent(decision) {
  if (!STORED_DECISIONS.has(decision)) return UNKNOWN;

  try {
    window.localStorage.setItem(APP_TRACKING_CONSENT_STORAGE_KEY, decision);
    return decision;
  } catch (error) {
    return UNKNOWN;
  }
}

function getMetaPixelState(fbq) {
  if (!fbq[META_PIXEL_STATE]) {
    fbq[META_PIXEL_STATE] = {
      initializedPixelIds: new Set(),
      scriptStatus: "loaded",
      failureListeners: new Set(),
    };
  }
  return fbq[META_PIXEL_STATE];
}

function revokeMetaPixelConsent() {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("consent", "revoke");
  }
}

function removeMetaPixelFailureListener(listener) {
  const state =
    typeof window !== "undefined" && typeof window.fbq === "function"
      ? window.fbq[META_PIXEL_STATE]
      : null;
  state?.failureListeners.delete(listener);
}

function activateMetaPixel(pixelId, { sendPageView, onScriptFailure }) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  let fbq = window.fbq;
  let ownsStub = false;
  let state;

  try {
    if (typeof fbq !== "function") {
      fbq = function metaPixelQueue() {
        if (fbq.callMethod) {
          fbq.callMethod.apply(fbq, arguments);
        } else {
          fbq.queue.push(arguments);
        }
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;
      ownsStub = true;

      state = getMetaPixelState(fbq);
      state.scriptStatus = "loading";

      const script = document.createElement("script");
      script.async = true;
      script.src = META_PIXEL_SCRIPT_URL;
      script.dataset.appDownloadMetaPixel = "true";
      script.onload = () => {
        state.scriptStatus = "loaded";
        state.failureListeners.clear();
      };
      script.onerror = () => {
        if (state.scriptStatus === "failed") return;
        state.scriptStatus = "failed";
        revokeMetaPixelConsent();
        state.failureListeners.forEach((listener) => listener());
        state.failureListeners.clear();
      };
      document.head.appendChild(script);
    }

    state = state || getMetaPixelState(fbq);
    if (state.scriptStatus === "failed") return false;
    if (state.scriptStatus === "loading") {
      state.failureListeners.add(onScriptFailure);
    }

    if (!state.initializedPixelIds.has(pixelId)) {
      fbq("set", "autoConfig", false, pixelId);
      fbq("init", pixelId);
      state.initializedPixelIds.add(pixelId);
    }
    fbq("consent", "grant");
    if (sendPageView) {
      fbq("trackSingle", pixelId, "PageView");
    }
    return true;
  } catch (error) {
    if (ownsStub) {
      delete window.fbq;
      delete window._fbq;
    }
    return false;
  }
}

function getTikTokPixelState(ttq) {
  if (!ttq[TIKTOK_PIXEL_STATE]) {
    ttq[TIKTOK_PIXEL_STATE] = {
      initializedPixelIds: new Set(),
      scriptStatus: "loaded",
      failureListeners: new Set(),
    };
  }
  return ttq[TIKTOK_PIXEL_STATE];
}

function createTikTokPixelQueue() {
  const ttq = [];
  ttq.methods = TIKTOK_METHODS;
  ttq.setAndDefer = (target, method) => {
    target[method] = function queueTikTokMethod() {
      target.push([method].concat(Array.from(arguments)));
    };
  };
  TIKTOK_METHODS.forEach((method) => ttq.setAndDefer(ttq, method));
  ttq.instance = (pixelId) => {
    const instance = ttq._i?.[pixelId] || [];
    TIKTOK_METHODS.forEach((method) => ttq.setAndDefer(instance, method));
    return instance;
  };
  ttq._i = {};
  ttq._t = {};
  ttq._o = {};
  window.TiktokAnalyticsObject = "ttq";
  window.ttq = ttq;
  return ttq;
}

function revokeTikTokPixelConsent() {
  if (
    typeof window !== "undefined" &&
    window.ttq &&
    typeof window.ttq.revokeConsent === "function"
  ) {
    window.ttq.revokeConsent();
  }
}

function removeTikTokPixelFailureListener(listener) {
  const state =
    typeof window !== "undefined" && window.ttq
      ? window.ttq[TIKTOK_PIXEL_STATE]
      : null;
  state?.failureListeners.delete(listener);
}

function activateTikTokPixel(pixelId, { sendPageView, onScriptFailure }) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  let ttq = window.ttq;
  let ownsQueue = false;
  let state;

  try {
    if (!ttq || typeof ttq.instance !== "function") {
      ttq = createTikTokPixelQueue();
      ownsQueue = true;
    }

    state = getTikTokPixelState(ttq);
    if (state.scriptStatus === "failed") return false;

    if (!state.initializedPixelIds.has(pixelId)) {
      ttq.holdConsent();
      ttq._i[pixelId] = ttq._i[pixelId] || [];
      ttq._i[pixelId]._u = TIKTOK_PIXEL_SCRIPT_URL;
      ttq._t[pixelId] = Date.now();
      ttq._o[pixelId] = {
        auto_config: false,
        historyObserver: false,
      };

      state.scriptStatus = "loading";
      state.failureListeners.add(onScriptFailure);

      const script = document.createElement("script");
      script.async = true;
      script.src = `${TIKTOK_PIXEL_SCRIPT_URL}?sdkid=${encodeURIComponent(
        pixelId
      )}&lib=ttq`;
      script.dataset.appDownloadTikTokPixel = "true";
      script.onload = () => {
        state.scriptStatus = "loaded";
        state.failureListeners.clear();
      };
      script.onerror = () => {
        if (state.scriptStatus === "failed") return;
        state.scriptStatus = "failed";
        revokeTikTokPixelConsent();
        state.failureListeners.forEach((listener) => listener());
        state.failureListeners.clear();
      };
      document.head.appendChild(script);
      state.initializedPixelIds.add(pixelId);
    } else if (state.scriptStatus === "loading") {
      state.failureListeners.add(onScriptFailure);
    }

    ttq.grantConsent();
    if (sendPageView) {
      ttq.instance(pixelId).page();
    }
    return true;
  } catch (error) {
    if (state) {
      state.scriptStatus = "failed";
      state.failureListeners.clear();
      revokeTikTokPixelConsent();
    } else if (ownsQueue) {
      delete window.ttq;
      delete window.TiktokAnalyticsObject;
    }
    return false;
  }
}

function copyForLanguage(languageCode, metaPixelId, tikTokPixelId) {
  const copy =
    String(languageCode || "").trim().toUpperCase() === "EN"
      ? COPY.EN
      : COPY.RO;
  const summary =
    metaPixelId && tikTokPixelId
      ? copy.summaryBoth
      : metaPixelId
        ? copy.summaryMeta
        : copy.summaryTikTok;
  return { ...copy, summary };
}

export default function AppTrackingConsent({
  metaPixelId,
  tikTokPixelId,
  languageCode,
  brandTheme,
  onTrackingChange,
}) {
  const copy = copyForLanguage(languageCode, metaPixelId, tikTokPixelId);
  const [decision, setDecision] = React.useState(readConsent);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [storageError, setStorageError] = React.useState(false);
  const initializationAttempted = React.useRef({ meta: false, tikTok: false });
  const pageViewSent = React.useRef({ meta: false, tikTok: false });
  const trackingActive = React.useRef(NO_ACTIVE_TRACKING);
  const showPanel = decision === UNKNOWN || settingsOpen;
  const choiceButtonSx = {
    minHeight: 36,
    px: 1.25,
    color: brandTheme.text,
    borderColor: "rgba(67,20,7,0.24)",
    bgcolor: "transparent",
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: brandTheme.text,
      bgcolor: brandTheme.background,
    },
    "& .MuiButton-startIcon": {
      mr: 0.5,
    },
  };
  const selectedChoiceButtonSx = {
    ...choiceButtonSx,
    borderColor: brandTheme.text,
    bgcolor: brandTheme.background,
    boxShadow: `inset 0 0 0 1px ${brandTheme.text}`,
  };

  const handleMetaScriptFailure = React.useCallback(() => {
    trackingActive.current = {
      ...trackingActive.current,
      meta: false,
    };
    onTrackingChange(trackingActive.current);
  }, [onTrackingChange]);

  const handleTikTokScriptFailure = React.useCallback(() => {
    trackingActive.current = {
      ...trackingActive.current,
      tikTok: false,
    };
    onTrackingChange(trackingActive.current);
  }, [onTrackingChange]);

  React.useEffect(() => {
    if (decision !== GRANTED) {
      trackingActive.current = NO_ACTIVE_TRACKING;
      onTrackingChange(NO_ACTIVE_TRACKING);
      return;
    }

    const active = { meta: false, tikTok: false };
    if (metaPixelId) {
      if (!initializationAttempted.current.meta) {
        initializationAttempted.current.meta = true;
        active.meta = activateMetaPixel(metaPixelId, {
          sendPageView: true,
          onScriptFailure: handleMetaScriptFailure,
        });
        pageViewSent.current.meta = active.meta;
      } else if (pageViewSent.current.meta) {
        active.meta = activateMetaPixel(metaPixelId, {
          sendPageView: false,
          onScriptFailure: handleMetaScriptFailure,
        });
      }
    }
    if (tikTokPixelId) {
      if (!initializationAttempted.current.tikTok) {
        initializationAttempted.current.tikTok = true;
        active.tikTok = activateTikTokPixel(tikTokPixelId, {
          sendPageView: true,
          onScriptFailure: handleTikTokScriptFailure,
        });
        pageViewSent.current.tikTok = active.tikTok;
      } else if (pageViewSent.current.tikTok) {
        active.tikTok = activateTikTokPixel(tikTokPixelId, {
          sendPageView: false,
          onScriptFailure: handleTikTokScriptFailure,
        });
      }
    }

    trackingActive.current = active;
    onTrackingChange(active);

    return () => {
      removeMetaPixelFailureListener(handleMetaScriptFailure);
      removeTikTokPixelFailureListener(handleTikTokScriptFailure);
      if (trackingActive.current.meta) {
        revokeMetaPixelConsent();
      }
      if (trackingActive.current.tikTok) revokeTikTokPixelConsent();
      trackingActive.current = NO_ACTIVE_TRACKING;
    };
  }, [
    decision,
    handleMetaScriptFailure,
    handleTikTokScriptFailure,
    metaPixelId,
    onTrackingChange,
    tikTokPixelId,
  ]);

  const saveDecision = (nextDecision) => {
    const savedDecision = writeConsent(nextDecision);
    setDecision(savedDecision);
    setStorageError(savedDecision !== nextDecision);
    if (savedDecision === nextDecision) setSettingsOpen(false);
  };

  return (
    <Box
      component="aside"
      aria-label={copy.settings}
      data-testid="app-tracking-consent"
      sx={
        showPanel
          ? {
              width: "100vw",
              marginLeft: "calc(50% - 50vw)",
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 0.75 },
              bgcolor: brandTheme.surface,
              borderBottom: "1px solid rgba(67,20,7,0.10)",
              fontFamily: "inherit",
            }
          : {
              position: "relative",
              zIndex: 5,
              height: 0,
            }
      }
    >
      <Stack
        sx={
          showPanel
            ? { width: "100%", maxWidth: 1120, mx: "auto" }
            : {
                position: "absolute",
                top: 6,
                right: { xs: 0, sm: 4 },
              }
        }
      >
        {showPanel ? (
          <Box
            component="section"
            aria-labelledby="app-tracking-consent-title"
          >
            <Stack spacing={0.5}>
              <Box>
                <Typography
                  id="app-tracking-consent-title"
                  component="h2"
                  sx={{
                    display: "inline",
                    mr: 0.75,
                    color: brandTheme.text,
                    fontWeight: 800,
                    fontSize: 14,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {settingsOpen ? copy.settingsTitle : copy.title}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: "text.secondary",
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  {copy.summary}
                </Typography>
              </Box>

              {storageError ? (
                <Typography role="alert" sx={{ color: "error.main", fontSize: 14 }}>
                  {copy.storageError}
                </Typography>
              ) : null}

              <Stack
                direction="row"
                columnGap={0.75}
                rowGap={0.5}
                alignItems="center"
                flexWrap="wrap"
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
                  aria-pressed={settingsOpen ? decision === GRANTED : undefined}
                  onClick={() => saveDecision(GRANTED)}
                  sx={
                    settingsOpen && decision === GRANTED
                      ? selectedChoiceButtonSx
                      : choiceButtonSx
                  }
                >
                  {copy.accept}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                  aria-pressed={settingsOpen ? decision === DENIED : undefined}
                  onClick={() => saveDecision(DENIED)}
                  sx={
                    settingsOpen && decision === DENIED
                      ? selectedChoiceButtonSx
                      : choiceButtonSx
                  }
                >
                  {copy.refuse}
                </Button>
                {settingsOpen ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setStorageError(false);
                      setSettingsOpen(false);
                    }}
                    sx={{
                      minHeight: 36,
                      px: 1,
                      color: brandTheme.text,
                      fontSize: 13,
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {copy.close}
                  </Button>
                ) : null}
                <Button
                  component={RouterLink}
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${copy.privacy} (${copy.newTab})`}
                  endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
                  size="small"
                  sx={{
                    minHeight: 36,
                    px: 0.75,
                    color: brandTheme.cta,
                    fontSize: 13,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    "& .MuiButton-endIcon": {
                      ml: 0.4,
                    },
                  }}
                >
                  {copy.privacy}
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Button
            size="small"
            startIcon={<PrivacyTipOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => setSettingsOpen(true)}
            sx={{
              alignSelf: "flex-end",
              color: brandTheme.text,
              bgcolor: brandTheme.surface,
              border: "1px solid rgba(67,20,7,0.12)",
              borderRadius: "8px",
              minHeight: 32,
              px: 1,
              fontSize: 12.5,
              fontWeight: 800,
              whiteSpace: "nowrap",
              opacity: 0.82,
              "& .MuiButton-startIcon": {
                mr: 0.4,
              },
              "&:hover": {
                bgcolor: brandTheme.surface,
                opacity: 1,
              },
            }}
          >
            {copy.settings}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
